import type { Node, Edge } from '@xyflow/react';
import type {
  SchemaDiagramData,
  Entity,
  Relationship,
  Cardinality,
} from '@veodiagram/shared';
import type { EntityNodeData } from './EntityNode';

// ============= Cluster Types =============

export interface SchemaCluster {
  id: string;
  label: string;
  entityIds: string[];
  color: string;
}

// Pastel cluster palette
const CLUSTER_COLORS = [
  '#dbeafe', // blue-100
  '#dcfce7', // green-100
  '#fce7f3', // pink-100
  '#fef3c7', // amber-100
  '#ede9fe', // violet-100
  '#cffafe', // cyan-100
  '#fef9c3', // yellow-100
  '#fee2e2', // red-100
];

// ============= Edge Style by Cardinality =============

function getRelationshipEdgeStyle(cardinality: Cardinality): {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
} {
  switch (cardinality) {
    case '1:1':
      return { stroke: '#6366f1', strokeWidth: 2 };
    case '1:N':
      return { stroke: '#22c55e', strokeWidth: 2 };
    case 'N:1':
      return { stroke: '#f59e0b', strokeWidth: 2 };
    case 'N:M':
      return { stroke: '#ec4899', strokeWidth: 2, strokeDasharray: '5 5' };
    default:
      return { stroke: '#64748b', strokeWidth: 2 };
  }
}

// ============= Convert Entity to React Flow Node =============

export function convertEntityToFlowNode(
  entity: Entity,
  overrides: Partial<EntityNodeData> = {}
): Node {
  return {
    id: entity.id,
    type: 'entity',
    position: entity.position,
    data: {
      label: entity.name,
      tableName: entity.tableName,
      columns: entity.columns,
      primaryKey: entity.primaryKey,
      description: entity.description,
      sourceType: entity.sourceType,
      evidence: entity.evidence || [],
      nodeId: entity.id,
      ...overrides,
    } as EntityNodeData,
  };
}

// ============= Convert Relationship to React Flow Edge =============

export function convertRelationshipToFlowEdge(
  relationship: Relationship,
  onEdgeClick?: (edgeId: string) => void
): Edge {
  const style = getRelationshipEdgeStyle(relationship.cardinality);

  return {
    id: relationship.id,
    source: relationship.from,
    target: relationship.to,
    type: 'crowsFoot',
    style: {
      stroke: style.stroke,
      strokeWidth: style.strokeWidth,
      strokeDasharray: style.strokeDasharray,
    },
    data: {
      cardinality: relationship.cardinality,
      fromColumn: relationship.fromColumn,
      toColumn: relationship.toColumn,
      isRequired: relationship.isRequired,
      onDelete: relationship.onDelete,
      onUpdate: relationship.onUpdate,
      onEdgeClick,
    },
  };
}

// ============= Full Schema Conversion =============

export interface SchemaConvertOptions {
  viewLevel?: 'macro' | 'mid' | 'micro';
  focusedEntityId?: string | null;
  searchHighlightIds?: Set<string>;
  showAnnotations?: boolean;
  annotations?: Record<string, { alias?: string; description?: string }>;
  onAnnotationChange?: (entityId: string, alias: string, description: string) => void;
  onEdgeClick?: (edgeId: string) => void;
}

/**
 * Convert SchemaDiagramData to React Flow nodes and edges.
 * Supports progressive disclosure, neighbourhood focus, search highlights.
 */
export function convertSchemaToReactFlow(
  data: SchemaDiagramData,
  options: SchemaConvertOptions = {}
): { nodes: Node[]; edges: Edge[] } {
  const {
    viewLevel = 'micro',
    focusedEntityId = null,
    searchHighlightIds = new Set<string>(),
    showAnnotations = true,
    annotations = {},
    onAnnotationChange,
    onEdgeClick,
  } = options;

  // Compute neighbourhood for focus opacity
  const neighbourIds = focusedEntityId
    ? getNeighbourIds(focusedEntityId, data.relationships)
    : null;

  const nodes: Node[] = data.entities.map(entity => {
    let focusOpacity = 1;
    if (focusedEntityId) {
      const isInNeighbourhood = entity.id === focusedEntityId || (neighbourIds?.has(entity.id) ?? false);
      focusOpacity = isInNeighbourhood ? 1 : 0.15;
    }

    const ann = annotations[entity.id] || {};

    return convertEntityToFlowNode(entity, {
      viewLevel,
      isSearchHighlight: searchHighlightIds.has(entity.id),
      focusOpacity,
      showAnnotations,
      alias: ann.alias || '',
      annotationDescription: ann.description || '',
      onAnnotationChange,
      nodeId: entity.id,
    });
  });

  const edges: Edge[] = data.relationships.map(rel => {
    const edgeInFocus =
      !focusedEntityId ||
      rel.from === focusedEntityId ||
      rel.to === focusedEntityId;

    const edge = convertRelationshipToFlowEdge(rel, onEdgeClick);
    return {
      ...edge,
      style: {
        ...edge.style,
        opacity: edgeInFocus ? 1 : 0.05,
      },
    };
  });

  return { nodes, edges };
}

// ============= Neighbourhood Helpers =============

/**
 * Get all entity IDs directly connected to the given entity.
 */
export function getNeighbourIds(entityId: string, relationships: Relationship[]): Set<string> {
  const neighbours = new Set<string>();
  for (const rel of relationships) {
    if (rel.from === entityId) neighbours.add(rel.to);
    if (rel.to === entityId)   neighbours.add(rel.from);
  }
  return neighbours;
}

// ============= Clustering =============

/**
 * Compute clusters from FK density using Union-Find.
 * Entities with ≥2 shared FK connections are merged into the same cluster.
 * Singletons are grouped by sourceFile.
 */
export function computeClusters(entities: Entity[], relationships: Relationship[]): SchemaCluster[] {
  // Build adjacency count: entity pair → count of shared FKs
  const edgeCount = new Map<string, number>();
  for (const rel of relationships) {
    const key = [rel.from, rel.to].sort().join('|');
    edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
  }

  // Union-Find
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    if (!parent.has(x)) parent.set(x, x);
    if (parent.get(x) !== x) parent.set(x, find(parent.get(x)!));
    return parent.get(x)!;
  };
  const union = (a: string, b: string) => {
    parent.set(find(a), find(b));
  };

  // Merge entities that share ≥1 FK connection (less strict than spec for better grouping)
  for (const rel of relationships) {
    union(rel.from, rel.to);
  }

  // Group by root
  const clusterMap = new Map<string, string[]>();
  for (const entity of entities) {
    const root = find(entity.id);
    if (!clusterMap.has(root)) clusterMap.set(root, []);
    clusterMap.get(root)!.push(entity.id);
  }

  // Build cluster objects
  const clusters: SchemaCluster[] = [];
  let colorIdx = 0;
  for (const [root, entityIds] of clusterMap) {
    if (entityIds.length < 2) continue; // skip singletons

    // Auto-label: find most common prefix in entity names
    const names = entityIds
      .map(id => entities.find(e => e.id === id)?.name || id)
      .sort();
    const label = deriveClusterLabel(names);

    clusters.push({
      id: `cluster-${root}`,
      label,
      entityIds,
      color: CLUSTER_COLORS[colorIdx % CLUSTER_COLORS.length],
    });
    colorIdx++;
  }

  return clusters;
}

function deriveClusterLabel(names: string[]): string {
  if (names.length === 0) return 'Group';
  if (names.length === 1) return names[0];

  // Find common prefix
  let prefix = names[0];
  for (const name of names.slice(1)) {
    while (!name.startsWith(prefix) && prefix.length > 0) {
      prefix = prefix.slice(0, -1);
    }
  }

  return prefix.trim() || names[0].split(/(?=[A-Z])/)[0] || 'Group';
}

// ============= Search Helpers =============

/**
 * Find entity IDs that contain a matching table name or column name.
 */
export function findSearchMatches(
  query: string,
  entities: Entity[]
): { entityIds: Set<string>; matches: Array<{ entityId: string; entityName: string; columnName?: string }> } {
  if (!query.trim()) {
    return { entityIds: new Set(), matches: [] };
  }

  const lowerQuery = query.toLowerCase();
  const entityIds = new Set<string>();
  const matches: Array<{ entityId: string; entityName: string; columnName?: string }> = [];

  for (const entity of entities) {
    // Table name match
    if (entity.name.toLowerCase().includes(lowerQuery) || entity.tableName?.toLowerCase().includes(lowerQuery)) {
      entityIds.add(entity.id);
      matches.push({ entityId: entity.id, entityName: entity.name });
      continue;
    }

    // Column name match
    for (const col of entity.columns) {
      if (col.name.toLowerCase().includes(lowerQuery)) {
        entityIds.add(entity.id);
        matches.push({ entityId: entity.id, entityName: entity.name, columnName: col.name });
        break; // one match per entity for the summary
      }
    }
  }

  return { entityIds, matches };
}

// ============= Reverse Conversion =============

/**
 * Convert React Flow state back to SchemaDiagramData.
 */
export function convertFlowToSchemaDiagram(
  nodes: Node[],
  edges: Edge[],
  metadata: SchemaDiagramData['metadata']
): SchemaDiagramData {
  const entities: Entity[] = nodes.map((node) => ({
    id: node.id,
    name: (node.data?.label as string) || node.id,
    tableName: node.data?.tableName as string | undefined,
    columns: (node.data?.columns as Entity['columns']) || [],
    primaryKey: (node.data?.primaryKey as string[]) || [],
    description: node.data?.description as string | undefined,
    position: node.position,
    sourceType: node.data?.sourceType as Entity['sourceType'],
  }));

  const relationships: Relationship[] = edges.map((edge) => ({
    id: edge.id,
    from: edge.source,
    to: edge.target,
    fromColumn: edge.data?.fromColumn as string | undefined,
    toColumn: edge.data?.toColumn as string | undefined,
    cardinality: (edge.data?.cardinality as Cardinality) || '1:N',
    label: typeof edge.label === 'string' ? edge.label : undefined,
    isRequired: (edge.data?.isRequired as boolean) || false,
    onDelete: edge.data?.onDelete as Relationship['onDelete'],
    onUpdate: edge.data?.onUpdate as Relationship['onUpdate'],
  }));

  return {
    metadata,
    entities,
    relationships,
  };
}

// ============= Layout =============

/**
 * Apply automatic layout to schema entities.
 * Uses a simple grid layout optimized for ER diagrams.
 */
export function applySchemaLayout(
  entities: Entity[],
  options: {
    entityWidth?: number;
    entityHeight?: number;
    horizontalGap?: number;
    verticalGap?: number;
    columnsPerRow?: number;
  } = {}
): Entity[] {
  const {
    entityWidth = 250,
    entityHeight = 200,
    horizontalGap = 80,
    verticalGap = 60,
    columnsPerRow = 4,
  } = options;

  return entities.map((entity, index) => {
    const col = index % columnsPerRow;
    const row = Math.floor(index / columnsPerRow);

    return {
      ...entity,
      position: {
        x: col * (entityWidth + horizontalGap),
        y: row * (entityHeight + verticalGap),
      },
    };
  });
}

// Re-export EntityNode types registry for ReactFlowRenderer
export { schemaNodeTypes } from './EntityNode';
